import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  useMediaQuery,
  CircularProgress,
  Alert,
  Container,
  Button,
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import axios from "axios";
import { useAuth } from "../../context/authContext";
import { url } from "../../constants/url";
import AppToast from "../../components/AppToast";

export default function ViewWPR() {
  const { projectId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Get data from navigation state
  const {
    weekType,
    weekStartDate: initialWeekStartDate,
    weekEndDate: initialWeekEndDate,
    projectName: propProjectName,
    cardData
  } = location.state || {};

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [project, setProject] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  
  // Week date state
  const [weekStartDate, setWeekStartDate] = useState(
    initialWeekStartDate ? new Date(initialWeekStartDate) : null
  );
  const [weekEndDate, setWeekEndDate] = useState(
    initialWeekEndDate ? new Date(initialWeekEndDate) : null
  );
  
  // Toast state
  const [toast, setToast] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });
  
  // Week data state
  const [weekData, setWeekData] = useState([]);
  const [wprId, setWprId] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Column widths matching EditWPR screen exactly
  const COLS = {
    week: "160px",
    activity: 1,
    details: 1.4,
    material: 1.3,
    support: 1.1,
    remark: 1.2,
  };

  // Format date functions
  const formatDateForWeekColumn = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const day = d.getDate();
    const suffix = getDaySuffix(day);
    const month = d.toLocaleString('default', { month: 'short' });
    const year = d.getFullYear().toString().slice(-2);
    const dayName = d.toLocaleString('default', { weekday: 'long' });
    
    return `${dayName} ${day}${suffix} ${month} '${year}`;
  };

  const formatDateForWeekHeader = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear().toString().slice(-2);
    return `${day}/${month}/${year}`;
  };
// Add this helper function for better date comparison
const formatDateForComparison = (date) => {
  if (!date) return "";
  const d = new Date(date);
  // Use UTC methods to avoid timezone issues
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
  const getDaySuffix = (day) => {
    if (day >= 11 && day <= 13) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  // Fetch project data and WPR data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const config = {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        };
        
        // Fetch project details
        const projectResponse = await axios.get(
          `${url}/api/inhouse-projects/fetch-by/${projectId}`,
          config
        );
        
        if (projectResponse.data.project) {
          setProject(projectResponse.data.project);
          
          // Fetch companies for PMC dropdown
          const companiesResponse = await axios.get(
            `${url}/api/company/fetch`,
            config
          );
          
          if (companiesResponse.data.data) {
            setCompanies(companiesResponse.data.data);
            // Select first company by default
            if (companiesResponse.data.data.length > 0) {
              setSelectedCompany(companiesResponse.data.data[0]._id);
            }
          }
          
          // If week dates are provided, fetch WPR data
          if (weekStartDate && weekEndDate) {
            await fetchWPRData();
          } else if (cardData && cardData.weekDates) {
            // Use card data if available
            setWeekStartDate(new Date(cardData.weekDates[0].date));
            setWeekEndDate(new Date(cardData.weekDates[5].date));
            await fetchWPRData();
          } else {
            setLoading(false);
          }
        } else {
          setError("Project not found");
          setLoading(false);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load project data. Please try again.");
        setLoading(false);
      }
    };
    
    if (projectId && token) {
      fetchData();
    }
  }, [projectId, token, weekStartDate, weekEndDate]);

  // Fetch WPR data for the selected week
//   const fetchWPRData = async () => {
//     try {
//       const config = {
//         headers: {
//           Authorization: token ? `Bearer ${token}` : undefined,
//         },
//       };
      
//       // Fetch all WPRs for this project
//       const wprResponse = await axios.get(
//         `${url}/api/wpr/fetch-by-project/${projectId}`,
//         config
//       );
      
//       if (wprResponse.data.wprs && wprResponse.data.wprs.length > 0) {
//         // Find WPR for the selected week
//         const weekStartStr = weekStartDate.toISOString().split('T')[0];
//         const weekEndStr = weekEndDate.toISOString().split('T')[0];
        
//         const existingWPR = wprResponse.data.wprs.find(wpr => {
//           if (!wpr.weekStartDate || !wpr.weekEndDate) return false;
          
//           const wprStart = new Date(wpr.weekStartDate).toISOString().split('T')[0];
//           const wprEnd = new Date(wpr.weekEndDate).toISOString().split('T')[0];
          
//           return wprStart === weekStartStr && wprEnd === weekEndStr;
//         });
        
//         if (existingWPR) {
//           setWprId(existingWPR._id);
//           // Generate week data with existing entries
//           generateWeekDataWithEntries(existingWPR.dailyEntries || []);
//         } else {
//           setError("WPR not found for the selected week");
//           setLoading(false);
//         }
//       } else {
//         setError("No WPRs found for this project");
//         setLoading(false);
//       }
//     } catch (err) {
//       console.error("Error fetching WPR data:", err);
//       setError("Failed to load WPR data. Please try again.");
//       setLoading(false);
//     }
//   };
const fetchWPRData = async () => {
  try {
    const config = {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
    };
    
    // Fetch all WPRs for this project
    const wprResponse = await axios.get(
      `${url}/api/wpr/fetch-by-project/${projectId}`,
      config
    );
    
    console.log("WPR Response:", wprResponse.data);
    console.log("Looking for week:", {
      weekStart: weekStartDate ? weekStartDate.toISOString().split('T')[0] : "No start date",
      weekEnd: weekEndDate ? weekEndDate.toISOString().split('T')[0] : "No end date"
    });
    
    if (wprResponse.data.wprs && wprResponse.data.wprs.length > 0) {
      // Find WPR for the selected week
      const normalizeDateForComparison = (date) => {
        if (!date) return null;
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d.getTime();
      };
      
      const targetWeekStart = normalizeDateForComparison(weekStartDate);
      const targetWeekEnd = normalizeDateForComparison(weekEndDate);
      
      console.log("Target dates normalized:", { targetWeekStart, targetWeekEnd });
      
      const existingWPR = wprResponse.data.wprs.find(wpr => {
        if (!wpr.weekStartDate || !wpr.weekEndDate) {
          console.log("WPR missing dates:", wpr._id);
          return false;
        }
        
        const wprStart = normalizeDateForComparison(wpr.weekStartDate);
        const wprEnd = normalizeDateForComparison(wpr.weekEndDate);
        
        console.log(`Checking WPR ${wpr._id}:`, {
          wprStart: wpr.weekStartDate,
          wprEnd: wpr.weekEndDate,
          normalized: { wprStart, wprEnd },
          matches: wprStart === targetWeekStart && wprEnd === targetWeekEnd
        });
        
        return wprStart === targetWeekStart && wprEnd === targetWeekEnd;
      });
      
      console.log("Found existing WPR:", existingWPR);
      
      if (existingWPR) {
        setWprId(existingWPR._id);
        // Generate week data with existing entries
        generateWeekDataWithEntries(existingWPR.dailyEntries || []);
      } else {
        console.log("No WPR found for this week. Available WPRs:", wprResponse.data.wprs.map(w => ({
          id: w._id,
          start: w.weekStartDate,
          end: w.weekEndDate,
          title: w.title
        })));
        setError("WPR not found for the selected week");
        setLoading(false);
      }
    } else {
      console.log("No WPRs found for this project");
      setError("No WPRs found for this project");
      setLoading(false);
    }
  } catch (err) {
    console.error("Error fetching WPR data:", err);
    console.error("Error details:", err.response?.data);
    setError("Failed to load WPR data. Please try again.");
    setLoading(false);
  }
};
  // Generate week data with existing entries
 // Update the generateWeekDataWithEntries function
const generateWeekDataWithEntries = (dailyEntries) => {
  console.log("Generating week data with entries:", dailyEntries);
  
  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const weekDataArray = [];
  
  const currentDate = new Date(weekStartDate);
  
  // Helper to normalize dates for comparison
  const normalizeDate = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  };
  
  for (let i = 0; i < 6; i++) {
    const date = new Date(currentDate);
    date.setDate(currentDate.getDate() + i);
    
    const dateNormalized = normalizeDate(date);
    
    console.log(`Processing day ${i} (${days[i]}):`, {
      originalDate: date,
      normalized: dateNormalized,
      dateStr: date.toISOString().split('T')[0]
    });
    
    // Find entries for this specific date
    const dayEntry = dailyEntries.find(entry => {
      if (!entry.date) {
        console.log(`Entry has no date:`, entry);
        return false;
      }
      
      const entryDate = new Date(entry.date);
      const entryDateNormalized = normalizeDate(entryDate);
      
      console.log(`  Comparing with entry date:`, {
        entryDate: entry.date,
        entryDateNormalized,
        matches: entryDateNormalized === dateNormalized && entry.day === days[i]
      });
      
      // Match by date and day name
      return entryDateNormalized === dateNormalized && entry.day === days[i];
    });
    
    console.log(`  Found day entry:`, dayEntry);
    
    weekDataArray.push({
      day: days[i],
      date: date,
      formattedDate: formatDateForWeekColumn(date),
      entries: dayEntry?.entries?.map(entry => ({
        _id: entry._id,
        targetedActivity: entry.targetedActivity || "",
        contractorVendor: entry.contractorVendor || "",
        details: entry.details || "",
        materialMachineryRequirements: entry.materialMachineryRequirements || "",
        supportNeeded: entry.supportNeeded || "",
        remarks: entry.remarks || "",
      })) || [],
    });
  }
  
  console.log("Generated week data:", weekDataArray);
  setWeekData(weekDataArray);
  setLoading(false);
};

  // Show toast function
  const showToast = (type, title, message) => {
    setToast({
      open: true,
      type,
      title,
      message,
    });
  };

  // Close toast function
  const closeToast = () => {
    setToast({ ...toast, open: false });
  };

  // Handle download WPR report
  const handleDownloadReport = async () => {
    try {
      if (!wprId) {
        showToast("error", "Error", "No WPR data available to download");
        return;
      }
      
      setDownloading(true);
      
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        responseType: 'blob'
      };
      
      const response = await axios.get(
        `${url}/api/wpr/download/${wprId}/report`,
        config
      );
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const urlBlob = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = urlBlob;
      
      // Generate filename
      const date = new Date(weekStartDate);
      const dateStr = date.toLocaleDateString('en-GB').replace(/\//g, '-');
      const projectName = project?.projectName?.replace(/[^a-zA-Z0-9]/g, '_') || 'WPR';
      link.download = `WPR_${projectName}_${dateStr}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(urlBlob);
      
      showToast("success", "Success", "WPR report downloaded successfully!");
      
    } catch (err) {
      console.error("Error downloading report:", err);
      showToast("error", "Error", "Failed to download report. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box p={2}>
        <Alert severity="warning">Project not found</Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" disableGutters sx={{ 
      p: { xs: 0.5, sm: 1, md: 1.5 },
      overflowX: "hidden" 
    }}>
  {/* ================= HEADER (Matching EditWPR) ================= */}
<Box mb={2}>
  <Box display="flex" justifyContent="space-between" mb={1}>
    {/* LEFT: TITLE + DOWNLOAD BUTTON */}
    <Box display="flex" alignItems="center" gap={1.5}>
      <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.SECONDARY }}>
        Weekly Planning Report (WPR) - View
      </Typography>

      {/* EDIT BUTTON - Similar to EditWPR style */}
      {/* {wprId && (
        <Button
          onClick={() => navigate(`/progress/edit-wpr/${projectId}`, {
            state: {
              projectId,
              weekType: location.state?.weekType,
              weekStartDate: weekStartDate,
              weekEndDate: weekEndDate,
              projectName: propProjectName || project?.projectName,
              cardData: location.state?.cardData
            }
          })}
          startIcon={<Icon name="edit" />}
          sx={{
            backgroundColor: COLORS.SECONDARY,
            color: "#fff",
            textTransform: "none",
            borderRadius: "7px",
            px: 2,
            height: 36,
            "&:hover": {
              backgroundColor: COLORS.PRIMARY,
            },
          }}
        >
          Edit
        </Button>
      )} */}
    </Box>

    {/* RIGHT: DOWNLOAD + CLOSE BUTTONS */}
    <Box display="flex" alignItems="center" gap={1}>
      {/* DOWNLOAD BUTTON - OUTLINED */}
      {wprId && (
        <Button
          onClick={handleDownloadReport}
          disabled={downloading}
          startIcon={
            downloading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <Icon name="download1" />
            )
          }
          variant="outlined"
          sx={{
            backgroundColor: COLORS.SECONDARY,
            color: "#fff",
            textTransform: "none",
            borderRadius: "7px",
            px: 2,
            height: 36,
            "&:hover": {
              backgroundColor: COLORS.PRIMARY,
            },
          }}
        >
          {downloading ? "Downloading..." : "Download"}
        </Button>
      )}

      {/* CLOSE BUTTON - Icon only */}
      <IconButton 
        size="small" 
        onClick={() => navigate(-1)}
        sx={{
        //   border: `1px solid ${COLORS.ERROR}`,
          color: COLORS.ERROR,
          "&:hover": {
            backgroundColor: "#FFEBEE",
          }
        }}
      >
        <Icon name="close3" />
      </IconButton>
    </Box>
  </Box>

  {/* Rest of the header information remains the same */}
  <Box display="flex" flexDirection="column" gap={0.4}>
    <Box display="flex" justifyContent="space-between" flexWrap={isMobile ? "wrap" : "nowrap"} gap={1}>
      <Typography sx={TYPOGRAPHY.BODY_LARGE}>
        <span style={{ fontWeight: 600, color: COLORS.TEXT }}>Project Name:</span>{" "}
        <span style={{ color: COLORS.SECONDARY }}>
          {propProjectName || project.projectName}
        </span>
      </Typography>

      <Box display="flex" alignItems="center" gap={1} flexWrap={isMobile ? "wrap" : "nowrap"}>
        <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, fontWeight: 600, color: COLORS.TEXT }}>
          Week:
        </Typography>
        
        <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
          {weekStartDate && weekEndDate 
            ? `${formatDateForWeekHeader(weekStartDate)} - ${formatDateForWeekHeader(weekEndDate)}`
            : "N/A"}
        </Typography>
      </Box>
    </Box>

    <Box display="flex" justifyContent="space-between" flexWrap={isMobile ? "wrap" : "nowrap"} gap={1}>
      <Typography sx={TYPOGRAPHY.BODY_LARGE}>
        <span style={{ fontWeight: 600, color: COLORS.TEXT }}>Address:</span>{" "}
        <span style={{ color: COLORS.SECONDARY }}>
          {project.siteAddress || "N/A"}
        </span>
      </Typography>

      <Typography sx={TYPOGRAPHY.BODY_LARGE}>
        <span style={{ fontWeight: 600, color: COLORS.TEXT }}>PMC:</span>{" "}
        <span style={{ color: COLORS.SECONDARY }}>
          {companies.find(c => c._id === selectedCompany)?.name || "Arconest Consultants"}
        </span>
      </Typography>
    </Box>

    <Box display="flex" justifyContent="space-between" flexWrap={isMobile ? "wrap" : "nowrap"} gap={1}>
      <Typography sx={TYPOGRAPHY.BODY_LARGE}>
        <span style={{ fontWeight: 600, color: COLORS.TEXT }}>Developer:</span>{" "}
        <span style={{ color: COLORS.SECONDARY }}>
          {project.developerName || "N/A"}
        </span>
      </Typography>

      <Typography sx={TYPOGRAPHY.BODY_LARGE}>
        <span style={{ fontWeight: 600, color: COLORS.TEXT }}>Engineer:</span>{" "}
        <span style={{ color: COLORS.SECONDARY }}>
          {project.assignedEngineer?.name || project.assignedEngineerName || "N/A"}
        </span>
      </Typography>
    </Box>
  </Box>

  {/* HEADER BOTTOM LINE */}
  <Box
    mt={1.5}
    height="1px"
    sx={{ backgroundColor: COLORS.LIGHT_SECONDARY }}
  />
</Box>

      {/* ================= TABLE HEADER ================= */}
      <Box
        display="flex"
        alignItems="flex-start"
        sx={{
          py: 1.2,
          ...TYPOGRAPHY.H4,
          color: COLORS.TEXT,
          borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
          backgroundColor: "#f8f9fa",
        }}
      >
        <FlexCell w={COLS.week} borderBottom="none">
          Week Days
        </FlexCell>

        <FlexCell flex={COLS.activity}>
          Targeted Activity <br /> Contractor/Vendor
        </FlexCell>
        <FlexCell flex={COLS.details}>Details</FlexCell>
        <FlexCell flex={COLS.material}>
          Material/Machinery <br /> requirements
        </FlexCell>
        <FlexCell flex={COLS.support}>Support Needed</FlexCell>
        <FlexCell flex={COLS.remark}>Remark</FlexCell>
      </Box>

      {/* ================= TABLE BODY ================= */}
      {weekData.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4, borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
          <Typography sx={{ color: COLORS.TEXT_SECONDARY, fontStyle: "italic" }}>
            No entries found for this week
          </Typography>
        </Box>
      ) : (
        weekData.map((dayBlock, dayIndex) => (
          <Box key={dayBlock.formattedDate}>
            {/* Render entries for this day */}
            {dayBlock.entries.map((row, entryIndex) => (
              <FlexRow key={entryIndex}>
                {/* WEEK COLUMN */}
                <FlexCell w={COLS.week} sx={{ height: "100%", display: "flex", alignItems: "center" }}>
                  {entryIndex === 0 && (
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        color: COLORS.SECONDARY,
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                      }}
                    >
                      {dayBlock.day.split(" ")[0]}
                      <br />
                      {dayBlock.formattedDate.split(" ").slice(1).join(" ")}
                    </Typography>
                  )}
                </FlexCell>

                {/* DATA COLUMNS */}
                <FlexRowInner isLastEntry={entryIndex === dayBlock.entries.length - 1}>
                  <FlexCell flex={COLS.activity} sx={{ display: "flex", alignItems: "center" }}>
                    <Typography sx={{ 
                      color: COLORS.SECONDARY,
                      ...TYPOGRAPHY.BODY_LARGE,
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                    }}>
                      {row.contractorVendor || "-"}
                    </Typography>
                  </FlexCell>
                  <FlexCell flex={COLS.details} sx={{ display: "flex", alignItems: "center" }}>
                    <Typography sx={{ 
                      color: COLORS.SECONDARY,
                      ...TYPOGRAPHY.BODY_LARGE,
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                    }}>
                      {row.details || "-"}
                    </Typography>
                  </FlexCell>
                  <FlexCell flex={COLS.material} sx={{ display: "flex", alignItems: "center" }}>
                    <Typography sx={{ 
                      color: COLORS.SECONDARY,
                      ...TYPOGRAPHY.BODY_LARGE,
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                    }}>
                      {row.materialMachineryRequirements || "-"}
                    </Typography>
                  </FlexCell>
                  <FlexCell flex={COLS.support} sx={{ display: "flex", alignItems: "center" }}>
                    <Typography sx={{ 
                      color: COLORS.SECONDARY,
                      ...TYPOGRAPHY.BODY_LARGE,
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                    }}>
                      {row.supportNeeded || "-"}
                    </Typography>
                  </FlexCell>
                  <FlexCell flex={COLS.remark} sx={{ display: "flex", alignItems: "center" }}>
                    <Typography sx={{ 
                      color: COLORS.SECONDARY,
                      ...TYPOGRAPHY.BODY_LARGE,
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                    }}>
                      {row.remarks || "-"}
                    </Typography>
                  </FlexCell>
                </FlexRowInner>
              </FlexRow>
            ))}

            {/* Show empty row if no entries */}
            {dayBlock.entries.length === 0 && (
              <FlexRow>
                <FlexCell w={COLS.week} sx={{ height: "100%", display: "flex", alignItems: "center" }}>
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_LARGE,
                      color: COLORS.SECONDARY,
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                    }}
                  >
                    {dayBlock.day.split(" ")[0]}
                    <br />
                    {dayBlock.formattedDate.split(" ").slice(1).join(" ")}
                  </Typography>
                </FlexCell>
                <FlexRowInner isLastEntry={true}>
                  <FlexCell flex={COLS.activity} sx={{ display: "flex", alignItems: "center" }} />
                  <FlexCell flex={COLS.details} sx={{ display: "flex", alignItems: "center" }} />
                  <FlexCell flex={COLS.material} sx={{ display: "flex", alignItems: "center" }} />
                  <FlexCell flex={COLS.support} sx={{ display: "flex", alignItems: "center" }} />
                  <FlexCell flex={COLS.remark} sx={{ display: "flex", alignItems: "center" }} />
                </FlexRowInner>
              </FlexRow>
            )}

            {/* DAY SEPARATOR (except for last day) */}
            {dayIndex < weekData.length - 1 && (
              <Box
                mt={2}
                height="1px"
                sx={{ backgroundColor: COLORS.LIGHT_SECONDARY }}
              />
            )}
          </Box>
        ))
      )}

      {/* BACK BUTTON */}
      {/* <Box display="flex" justifyContent="center" mt={3}>
        <Button
          variant="outlined"
          size="large"
          startIcon={<Icon name="arrowLeft" />}
          sx={{
            borderColor: COLORS.SECONDARY,
            color: COLORS.SECONDARY,
            textTransform: "none",
            px: 4,
            "&:hover": {
              borderColor: COLORS.PRIMARY,
              color: COLORS.PRIMARY,
            },
          }}
          onClick={() => navigate(-1)}
        >
          Back to Project Overview
        </Button>
      </Box> */}

      {/* Toast Component */}
      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={closeToast}
      />
    </Container>
  );
}

/* ================= REUSABLE COMPONENTS ================= */

const FlexRow = ({ children, header }) => (
  <Box
    display="flex"
    alignItems="stretch"
    sx={{
      py: header ? 1.2 : 1,
      fontWeight: header ? 600 : 400,
    }}
  >
    {children}
  </Box>
);

const FlexCell = ({ children, flex, w, sx = {}, borderBottom = "none", ...props }) => (
  <Box 
    flex={flex} 
    width={w} 
    px={1} 
    sx={{ 
      ...TYPOGRAPHY.BODY_LARGE,
      borderBottom: borderBottom,
      ...sx
    }}
    {...props}
  >
    {children}
  </Box>
);

const FlexRowInner = ({ children, header, isLastEntry = false }) => (
  <Box
    display="flex"
    flex={1}
    alignItems="stretch"
    sx={{
      borderBottom: isLastEntry ? 'none' : `1px solid ${COLORS.LIGHT_SECONDARY}`,
    }}
  >
    {children}
  </Box>
);